/**
 * SanctaRecognizedChip — a friendly PDP chip that surfaces when Sancta's
 * memory ledger for the current user contains a preference/fact that maps
 * to this product.
 *
 * Uses a simple keyword scan (no LLM roundtrip). Matches keywords against:
 *   · product.title, product.brand, product.category, product.description
 *
 * Renders NOTHING when there is no signed-in user or no matching memory.
 * This keeps the chip low-noise: it only appears when it genuinely helps.
 */
import React, { useEffect, useState } from "react";
import { BrainCircuit, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

interface Memory {
  memory_id: string;
  category: "preference" | "fact" | "follow_up";
  text: string;
  pinned?: boolean;
}

interface Product {
  title?: string | null;
  brand?: string | null;
  category?: string | null;
  sub_category?: string | null;
  description?: string | null;
  short_description?: string | null;
}

// Tiny stop-word list so trivial words don't cause false positives.
const STOP = new Set([
  "i", "a", "an", "and", "the", "is", "are", "am", "for", "with", "of",
  "to", "my", "at", "on", "in", "have", "has", "had", "do", "does", "did",
  "always", "never", "prefer", "only", "want", "need", "buying", "buy",
  "sancta", "socibaba", "please", "would", "could", "should", "if", "so",
  "very", "really", "just", "one", "some", "any", "many", "much", "each",
  "over", "under", "than", "then", "now", "later", "ago", "will", "wo",
  "kids", "kid", "child", "children", "years", "year", "old", "under",
]);

// Extract candidate lemmas from a memory sentence — lowercase words >= 4
// chars that are not stop-words, without duplicates.
function tokensFromMemory(text: string): string[] {
  return Array.from(
    new Set(
      text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, " ")
        .split(/\s+/)
        .filter(w => w.length >= 4 && !STOP.has(w))
    )
  );
}

function productHaystack(p: Product): string {
  return [
    p.title, p.brand, p.category, p.sub_category, p.description, p.short_description,
  ].filter(Boolean).join(" • ").toLowerCase();
}

export const SanctaRecognizedChip: React.FC<{ product: Product | null | undefined }> = ({ product }) => {
  const { user } = useAuth();
  const [memories, setMemories] = useState<Memory[]>([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/sancta/memory");
        if (!cancelled) setMemories(data.memories || []);
      } catch { /* silent — chip simply won't render */ }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id]);

  if (!user || !product || memories.length === 0) return null;

  const hay = productHaystack(product);
  // Score each memory by how many of its meaningful tokens land in the
  // product haystack. Only surface the top match with ≥2 hits OR ≥1 hit
  // for a pinned/follow-up memory.
  const scored = memories
    .map(m => {
      const toks = tokensFromMemory(m.text);
      let hits = 0;
      const hitWords: string[] = [];
      toks.forEach(t => { if (hay.includes(t)) { hits += 1; hitWords.push(t); } });
      return { m, hits, hitWords };
    })
    .filter(r => r.hits >= 2 || (r.hits >= 1 && (r.m.pinned || r.m.category === "follow_up")))
    .sort((a, b) => b.hits - a.hits);

  const top = scored[0];
  if (!top) return null;

  const catLabel = top.m.category === "follow_up"
    ? "follow-up you asked about"
    : top.m.category === "fact"
      ? "fact about you"
      : "preference of yours";

  return (
    <div
      data-testid="pdp-sancta-recognized"
      className="mt-4 rounded-xl border border-fuchsia-200 dark:border-fuchsia-800/40 bg-gradient-to-r from-fuchsia-50 to-cyan-50 dark:from-fuchsia-950/20 dark:to-cyan-950/20 p-3 flex items-start gap-2.5"
    >
      <div className="shrink-0 mt-0.5">
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-500 grid place-items-center text-white">
          <BrainCircuit className="h-4 w-4" />
        </div>
      </div>
      <div className="flex-1 text-sm">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-fuchsia-600 dark:text-fuchsia-300">
          <Sparkles className="h-3 w-3" /> Sancta noticed
        </div>
        <div className="mt-1 text-neutral-800 dark:text-slate-100 leading-snug">
          This matches a <span className="font-semibold">{catLabel}</span>:
          <span className="italic text-neutral-600 dark:text-slate-300"> "{top.m.text}"</span>
        </div>
      </div>
    </div>
  );
};

export default SanctaRecognizedChip;
